import React, { useState } from 'react';
import { Phone, Instagram, Shield, Clock, Globe, Heart, MessageCircle } from 'lucide-react';
import { mockData } from '../data/mock';

const PsychologistWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleWhatsAppClick = () => {
    const phone = mockData.contact.whatsapp.replace(/\D/g, '');
    const message = encodeURIComponent(mockData.contact.whatsappMessage);
    const whatsappUrl = `https://wa.me/55${phone}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleInstagramClick = () => {
    window.open(`https://instagram.com/${mockData.contact.instagram}`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="header-nav">
        <div className="container">
          <div className="flex items-center justify-between w-full">
            <div className="text-xl font-semibold" style={{ color: 'var(--terracotta-500)' }}>
              Rafaela Leopoldino
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('inicio')}
                className="text-sm font-medium hover:text-[var(--terracotta-500)] transition-colors"
              >
                Início
              </button>
              <button 
                onClick={() => scrollToSection('sobre-tcc')}
                className="text-sm font-medium hover:text-[var(--terracotta-500)] transition-colors"
              >
                Sobre TCC
              </button>
              <button 
                onClick={() => scrollToSection('atendimento')}
                className="text-sm font-medium hover:text-[var(--terracotta-500)] transition-colors"
              >
                Atendimento
              </button>
              <button 
                onClick={() => scrollToSection('contato')}
                className="text-sm font-medium hover:text-[var(--terracotta-500)] transition-colors"
              >
                Contato
              </button>
            </nav>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="w-6 h-6 flex flex-col justify-center space-y-1">
                <span className={`block h-0.5 w-6 bg-current transition-transform ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
                <span className={`block h-0.5 w-6 bg-current transition-opacity ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block h-0.5 w-6 bg-current transition-transform ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
              </div>
            </button>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <nav className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t">
              <div className="py-4 space-y-2">
                <button 
                  onClick={() => scrollToSection('inicio')}
                  className="block w-full text-left px-4 py-2 text-sm font-medium hover:bg-gray-50"
                >
                  Início
                </button>
                <button 
                  onClick={() => scrollToSection('sobre-tcc')}
                  className="block w-full text-left px-4 py-2 text-sm font-medium hover:bg-gray-50"
                >
                  Sobre TCC
                </button>
                <button 
                  onClick={() => scrollToSection('atendimento')}
                  className="block w-full text-left px-4 py-2 text-sm font-medium hover:bg-gray-50"
                >
                  Atendimento
                </button>
                <button 
                  onClick={() => scrollToSection('contato')}
                  className="block w-full text-left px-4 py-2 text-sm font-medium hover:bg-gray-50"
                >
                  Contato
                </button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="hero-section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left side - Text content */}
              <div className="space-y-6 order-2 md:order-1">
                <div className="space-y-4">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-[var(--text-primary)] leading-tight">
                    Olá! Sou a Rafaela Leopoldino - Psicóloga Clínica.
                  </h1>
                  <div className="space-y-2">
                    <p 
                      className="text-[var(--text-secondary)] font-medium"
                      style={{
                        fontFamily: "'Playfair Display', 'Georgia', serif",
                        fontSize: '1.1rem',
                        fontWeight: '500'
                      }}
                    >
                      CRP 08/45852
                    </p>
                    <p 
                      className="body-large text-[var(--text-secondary)]"
                      style={{
                        fontFamily: "'Playfair Display', 'Georgia', serif",
                        fontStyle: 'italic',
                        fontSize: '1.2rem',
                        lineHeight: '1.6',
                        fontWeight: '400'
                      }}
                    >
                      {mockData.psychologist.greeting}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={handleWhatsAppClick}
                    className="btn-primary hover-scale"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Vamos descobrir juntos?
                  </button>
                  <button 
                    onClick={handleInstagramClick}
                    className="btn-secondary hover-scale"
                  >
                    <Instagram className="w-5 h-5 mr-2" />
                    Instagram
                  </button>
                </div>
              </div>

              {/* Right side - Rafaela's Photo - Sempre visível */}
              <div className="flex justify-center order-1 md:order-2">
                <div className="w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-lg hover-lift flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_mind-wellness-11/artifacts/r1o0k97z_IMG-20250730-WA0009.jpg"
                    alt="Rafaela - Psicóloga Clínica"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About TCC Section */}
      <section id="sobre-tcc" className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="heading-1 mb-6">Sobre a Terapia Cognitivo-Comportamental (TCC)</h2>
              <p className="body-large text-[var(--text-secondary)] max-w-2xl mx-auto">
                {mockData.approach.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {mockData.approach.benefits.map((benefit, index) => (
                <div key={index} className="info-card accent-terracotta hover-lift">
                  <h3 className="heading-3 mb-3">{benefit.title}</h3>
                  <p className="body-medium text-[var(--text-secondary)]">{benefit.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <div className="info-card accent-beige max-w-2xl mx-auto">
                <h3 className="heading-3 mb-3">Como funciona?</h3>
                <p className="body-medium text-[var(--text-secondary)]">
                  {mockData.approach.howItWorks}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="atendimento" className="py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="heading-1 mb-6">Sobre os Atendimentos</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {mockData.services.map((service, index) => (
                <div key={index} className="info-card accent-sand hover-lift text-center">
                  <div className="w-12 h-12 rounded-full bg-[var(--terracotta-500)] mx-auto mb-4 flex items-center justify-center">
                    {index === 0 && <Globe className="w-6 h-6 text-white" />}
                    {index === 1 && <Clock className="w-6 h-6 text-white" />}
                    {index === 2 && <Shield className="w-6 h-6 text-white" />}
                  </div>
                  <h3 className="heading-3 mb-3">{service.title}</h3>
                  <p className="body-medium text-[var(--text-secondary)]">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ethics Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="info-card accent-beige">
              <div className="w-16 h-16 rounded-full bg-[var(--terracotta-500)] mx-auto mb-6 flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h2 className="heading-2 mb-4">Código de Ética e Sigilo</h2>
              <p className="body-large text-[var(--text-secondary)] leading-relaxed">
                {mockData.ethics.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="heading-1 mb-6">Entre em Contato</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="info-card accent-terracotta hover-lift">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--terracotta-500)] flex items-center justify-center mr-4">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="heading-3">WhatsApp</h3>
                    <p className="body-small text-[var(--text-muted)]">Resposta rápida</p>
                  </div>
                </div>
                <p className="body-medium text-[var(--text-secondary)] mb-4">
                  {mockData.contact.whatsapp}
                </p>
                <button 
                  onClick={handleWhatsAppClick}
                  className="btn-primary hover-scale w-full"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Enviar Mensagem
                </button>
              </div>

              <div className="info-card accent-sand hover-lift">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--terracotta-500)] flex items-center justify-center mr-4">
                    <Instagram className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="heading-3">Instagram</h3>
                    <p className="body-small text-[var(--text-muted)]">Conteúdo e dicas</p>
                  </div>
                </div>
                <p className="body-medium text-[var(--text-secondary)] mb-4">
                  Me conheça mais e vamos conversar pelo Instagram também!
                </p>
                <button 
                  onClick={handleInstagramClick}
                  className="btn-secondary hover-scale w-full"
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  @{mockData.contact.instagram}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Section */}
      <section className="py-20 relative overflow-hidden" style={{ minHeight: '600px' }}>
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mind-wellness-11/artifacts/tamxtm7s_IMG-20250730-WA0010.jpg)',
            filter: 'brightness(0.5)'
          }}
        />
        
        {/* Gradient overlay at bottom for better text readability */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, transparent 0%, transparent 50%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.8) 90%, rgba(0,0,0,0.9) 100%)'
          }}
        />
        
        {/* Content positioned at very bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-10 pb-6">
          <div className="container">
            <div className="space-y-4 text-center">
              <h2 
                className="text-white font-bold"
                style={{
                  fontSize: '2.2rem',
                  lineHeight: '1.3',
                  fontWeight: '700'
                }}
              >
                {mockData.finalMessage.quote}
              </h2>
              <p 
                className="text-white font-semibold"
                style={{
                  fontSize: '1.5rem',
                  fontWeight: '600'
                }}
              >
                {mockData.finalMessage.signature}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[var(--text-primary)] text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-[var(--terracotta-400)]">
                  Rafaela Leopoldino - Psicóloga Clínica
                </h3>
                <p className="text-white text-base">
                  {mockData.psychologist.crp}
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-4 text-white text-lg">Contato</h4>
                <div className="space-y-2">
                  <p className="text-gray-100 text-base">WhatsApp: {mockData.contact.whatsapp}</p>
                  <p className="text-gray-100 text-base">Instagram: @{mockData.contact.instagram}</p>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-8 pt-8 text-center">
              <p className="text-gray-200 text-base">
                © 2024 Rafaela Leopoldino - Psicóloga Clínica. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PsychologistWebsite;